import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const ReviewsContext = createContext();

export const useReviews = () => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within ReviewsProvider');
  }
  return context;
};

export const ReviewsProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to reviews in real-time
  useEffect(() => {
    const q = query(collection(db, 'reviews'), orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReviews(reviewsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching reviews:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add new review (typically from customer side, but can be manual)
  const addReview = async (reviewData) => {
    try {
      const newReview = {
        ...reviewData,
        timestamp: Timestamp.now(),
        status: 'pending',
        adminResponse: null
      };

      const docRef = await addDoc(collection(db, 'reviews'), newReview);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding review:', error);
      return { success: false, error };
    }
  };

  // Add admin response to review
  const respondToReview = async (reviewId, responseText) => {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      await updateDoc(reviewRef, {
        adminResponse: responseText,
        status: 'responded',
        respondedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error responding to review:', error);
      return { success: false, error };
    }
  };

  // Filter reviews by rating
  const filterByRating = (rating) => {
    if (rating === 'all') return reviews;
    return reviews.filter(r => r.rating === parseInt(rating));
  };

  // Get average rating
  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return sum / reviews.length;
  };

  // Get rating counts
  const getRatingCounts = () => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      if (counts[r.rating] !== undefined) {
        counts[r.rating]++;
      }
    });
    return counts;
  };

  // Get stats
  const getStats = () => {
    return {
      totalReviews: reviews.length,
      averageRating: getAverageRating(),
      ratingCounts: getRatingCounts(),
      pendingReviews: reviews.filter(r => r.status === 'pending').length,
      respondedReviews: reviews.filter(r => r.status === 'responded').length
    };
  };

  const value = {
    reviews,
    loading,
    addReview,
    respondToReview,
    filterByRating,
    getAverageRating,
    getRatingCounts,
    getStats
  };

  return (
    <ReviewsContext.Provider value={value}>
      {children}
    </ReviewsContext.Provider>
  );
};
